import React, { useState, useEffect, useMemo } from 'react'
import Container from '../../Components/Container'
import { Chart } from 'react-google-charts'
import { FaChartPie, FaArrowTrendUp } from 'react-icons/fa6'
import { MdOutlineBarChart } from 'react-icons/md'
import toast, { Toaster } from 'react-hot-toast'

const readJson = async (res) => {
  if (!res.ok) throw new Error('Sunucu yanıt vermedi')
  return res.json()
}

const isApiError = (data) =>
  data != null &&
  typeof data === 'object' &&
  !Array.isArray(data) &&
  data.status === 400

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  return []
}

const unwrapMonthRow = (item) => {
  if (item == null) return {}
  if (Array.isArray(item) && item[0] != null && typeof item[0] === 'object') {
    return item[0]
  }
  if (typeof item === 'object' && !Array.isArray(item)) return item
  return {}
}

const toNum = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Sunucu ASCII veya Türkçe karakterli anahtar döndürebilir */
const pickField = (row, keys) => {
  if (row == null || typeof row !== 'object') return 0
  for (const k of keys) {
    if (
      Object.prototype.hasOwnProperty.call(row, k) &&
      row[k] != null &&
      row[k] !== ''
    ) {
      return toNum(row[k])
    }
  }
  return 0
}

const StatCard = ({ icon, title, desc, children }) => (
  <div className='relative flex min-h-[420px] flex-col items-center overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm'>
    <div className='mb-2 flex items-center gap-3'>
      <span className='text-2xl text-indigo-500'>{icon}</span>
      <h2 className='text-lg font-bold text-slate-800'>{title}</h2>
    </div>
    {desc && (
      <p className='mb-4 text-center text-sm text-slate-500'>{desc}</p>
    )}
    <div className='flex w-full flex-1 items-center justify-center'>
      {children}
    </div>
  </div>
)

const Loader = () => (
  <div className='flex h-72 w-full flex-col items-center justify-center'>
    <div className='mb-2 h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-500' />
    <span className='text-sm text-slate-400'>Yükleniyor…</span>
  </div>
)

const EmptyChart = () => (
  <div className='w-full text-center text-sm text-slate-400'>Veri bulunamadı</div>
)

const pieSliceColors = [
  '#6366f1',
  '#818cf8',
  '#a5b4fc',
  '#c7d2fe',
  '#4f46e5',
  '#4338ca',
  '#3730a3',
  '#312e81',
  '#22c55e',
  '#86efac',
]

const isPieDataValid = (data) =>
  Array.isArray(data) &&
  data.length > 0 &&
  data.some((item) => {
    if (item == null || typeof item !== 'object') return false
    const label = String(item.title ?? item.name ?? '').trim()
    if (!label) return false
    const karVal = item.kar
    return karVal != null && karVal !== '' && Number.isFinite(Number(karVal))
  })

const convertDataForChart = (data) => {
  const chartData = [['Ürün', 'Kâr']]
  if (!Array.isArray(data)) return chartData
  data.forEach((item) => {
    const label = String(item?.title ?? item?.name ?? '—').trim() || '—'
    chartData.push([label, toNum(item?.kar)])
  })
  return chartData
}

const convertToBarChartData = (data) => {
  const rows = Array.isArray(data) ? data : []
  const chartData = [['Ay', 'İade', 'Satış', 'Zayiat']]
  rows.forEach((item, index) => {
    const o = unwrapMonthRow(item)
    const iade = pickField(o, [
      'iade',
      'ıade',
      'Iade',
      'İade',
      'return_count',
    ])
    const satis = pickField(o, [
      'satis',
      'satış',
      'satıs',
      'Satis',
      'sales',
    ])
    const zayiat = pickField(o, [
      'zayiat',
      'zaiyat',
      'zaıat',
      'Zayiat',
      'zayi',
    ])
    chartData.push([`Ay ${index + 1}`, iade, satis, zayiat])
  })
  return chartData
}

const convertToBarChartDataProfit = (data) => {
  const rows = Array.isArray(data) ? data : []
  const chartData = [['Ay', 'Kâr', 'Satış', 'Maliyet']]
  rows.forEach((item, index) => {
    const o = unwrapMonthRow(item)
    const kar = pickField(o, ['kar', 'Kar', 'profit'])
    const satis = pickField(o, [
      'satis',
      'satış',
      'satıs',
      'Satis',
      'sales',
    ])
    const maliyet = pickField(o, ['maliyet', 'Maliyet', 'cost'])
    chartData.push([`Ay ${index + 1}`, kar, satis, maliyet])
  })
  return chartData
}

const hasBarRows = (chartData) =>
  Array.isArray(chartData) && chartData.length > 1

const Statistics = () => {
  const [maxProfit, setMaxProfit] = useState([])
  const [minProfit, setMinProfit] = useState([])
  const [monthlyFlow, setMonthlyFlow] = useState([])
  const [profitSeries, setProfitSeries] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    const fetchAll = async () => {
      try {
        const fetchData = async (action, extra = {}) => {
          const formdata = new FormData()
          formdata.append('action', action)
          if (extra.date != null) formdata.append('date', String(extra.date))
          const res = await fetch(
            process.env.REACT_APP_BASE_URL + 'statistics.php',
            { method: 'POST', body: formdata }
          )
          return readJson(res)
        }

        const year = new Date().getFullYear()
        const [maxRaw, minRaw, floatRaw, karRaw] = await Promise.all([
          fetchData('max'),
          fetchData('min'),
          fetchData('float', { date: year }),
          fetchData('kar', { date: year }),
        ])

        if (cancelled) return

        const assignList = (raw, setter) => {
          if (isApiError(raw)) {
            setter([])
            return
          }
          setter(normalizeList(raw))
        }

        assignList(maxRaw, setMaxProfit)
        assignList(minRaw, setMinProfit)
        assignList(floatRaw, setMonthlyFlow)
        assignList(karRaw, setProfitSeries)
      } catch {
        if (!cancelled) {
          toast.error('İstatistikler yüklenirken bir hata oluştu')
          setMaxProfit([])
          setMinProfit([])
          setMonthlyFlow([])
          setProfitSeries([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    fetchAll()
    return () => {
      cancelled = true
    }
  }, [])

  const barTransactions = useMemo(
    () => convertToBarChartData(monthlyFlow),
    [monthlyFlow]
  )
  const barProfit = useMemo(
    () => convertToBarChartDataProfit(profitSeries),
    [profitSeries]
  )

  const barTransactionsOk = hasBarRows(barTransactions)
  const barProfitOk = hasBarRows(barProfit)

  return (
    <Container padding bg='#f8fafc'>
      <Toaster
        position='top-right'
        toastOptions={{ className: 'text-sm font-medium shadow-lg' }}
      />

      <div className='mx-auto w-full max-w-[1400px] space-y-8 px-4 pb-16 pt-2 sm:px-6 lg:px-8'>
        <h1 className='text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl'>
          İstatistikler
        </h1>

        <div className='grid w-full grid-cols-1 gap-8 md:grid-cols-2'>
          <StatCard
            icon={<FaChartPie />}
            title='En karlı 10 ürün'
            desc='Kâra göre en iyi 10 ürünün dağılımı'
          >
            {loading ? (
              <Loader />
            ) : !isPieDataValid(maxProfit) ? (
              <EmptyChart />
            ) : (
              <Chart
                chartType='PieChart'
                data={convertDataForChart(maxProfit)}
                options={{
                  legend: { position: 'bottom' },
                  chartArea: { width: '90%', height: '80%' },
                  pieSliceText: 'value',
                  colors: pieSliceColors,
                }}
                width='100%'
                height='300px'
              />
            )}
          </StatCard>
          <StatCard
            icon={<FaChartPie className='rotate-180' />}
            title='En karsız 10 ürün'
            desc='Kâra göre en düşük 10 ürünün dağılımı'
          >
            {loading ? (
              <Loader />
            ) : !isPieDataValid(minProfit) ? (
              <EmptyChart />
            ) : (
              <Chart
                chartType='PieChart'
                data={convertDataForChart(minProfit)}
                options={{
                  legend: { position: 'bottom' },
                  chartArea: { width: '90%', height: '80%' },
                  animation: { duration: 1000, easing: 'out', startup: true },
                  pieSliceText: 'value',
                  colors: [...pieSliceColors].reverse(),
                }}
                width='100%'
                height='300px'
              />
            )}
          </StatCard>
        </div>

        <div className='grid w-full grid-cols-1 gap-8 md:grid-cols-2'>
          <StatCard
            icon={<MdOutlineBarChart />}
            title='Aylık işlem sayıları'
            desc='Her ay yapılan satış, iade ve zayiat işlemleri'
          >
            {loading ? (
              <Loader />
            ) : !barTransactionsOk ? (
              <EmptyChart />
            ) : (
              <Chart
                chartType='Bar'
                width='100%'
                height='300px'
                data={barTransactions}
                options={{
                  legend: { position: 'bottom' },
                  chartArea: { width: '80%', height: '70%' },
                  vAxis: { minValue: 0 },
                  animation: { duration: 300, easing: 'out', startup: true },
                  colors: ['#6366f1', '#22c55e', '#f87171'],
                }}
              />
            )}
          </StatCard>
          <StatCard
            icon={<FaArrowTrendUp />}
            title='Kâr, satış ve maliyet'
            desc='Aylık toplam kâr, satış ve maliyet karşılaştırması'
          >
            {loading ? (
              <Loader />
            ) : !barProfitOk ? (
              <EmptyChart />
            ) : (
              <Chart
                chartType='Bar'
                width='100%'
                height='300px'
                data={barProfit}
                options={{
                  legend: { position: 'bottom' },
                  chartArea: { width: '80%', height: '70%' },
                  hAxis: {
                    title: 'Toplam',
                    minValue: 0,
                    format: 'decimal',
                  },
                  bars: 'horizontal',
                  bar: { groupWidth: '75%' },
                  colors: ['#6366f1', '#22c55e', '#fbbf24'],
                }}
              />
            )}
          </StatCard>
        </div>
      </div>
    </Container>
  )
}

export default Statistics
